<template>
  <div class="chat-flashcards-container">
    <!-- List Mode -->
    <div v-if="mode === 'list'" class="flashcards-list">
      <div class="d-flex justify-content-between align-items-center mb-4">
        <h4 class="mb-3">{{ t('chat.flashcards') }}</h4>
        <div class="btn-group">
          <button @click="emit('showChat')" class="btn btn-outline-primary btn-sm">
            <i class="bi bi-chat-dots"></i> {{ t('chat.show-chat') }}
          </button>
          <button 
            @click="clearFlashcards" 
            class="btn btn-outline-danger btn-sm"
            :disabled="!flashcards.length || isClearing"
          >
            <div v-if="isClearing" class="spinner-border spinner-border-sm me-1" role="status">
              <span class="visually-hidden">{{ t('chat.loading') }}</span>
            </div>
            <i v-else class="bi bi-eraser"></i> {{ t('chat.clear') || 'Clear' }}
          </button>
          <button 
            @click="mode = 'game'" 
            class="btn btn-primary btn-sm"
            :disabled="!flashcards.length"
          >
            <i class="bi bi-play-circle"></i> {{ t('chat.quiz') || 'Quiz' }}
          </button>
        </div>
      </div>
      
      <!-- Loading State -->
      <div v-if="isLoading" class="text-center py-4">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">{{ t('chat.loading') }}</span>
        </div>
      </div>
      
      <!-- Empty State -->
      <div v-else-if="!flashcards.length" class="text-center py-4">
        <div class="alert alert-info">
          <i class="bi bi-info-circle"></i>
          {{ t('chat.no-flashcards-yet-try-asking-the-tutor-to') }}
        </div>
      </div>
      
      <!-- Flashcards List -->
      <div v-else class="flashcards-grid">
        <div 
          v-for="flashcard in flashcards" 
          :key="flashcard.id" 
          class="flashcard-item card mb-2"
          @click="selectFlashcard(flashcard)"
        >            <div class="card-body">
              <div class="d-flex justify-content-between align-items-start">
                <div class="flashcard-content flex-grow-1" v-if="flashcard.editing">
                  <div class="flashcard-front mb-2">
                    <input 
                      v-model="flashcard.front" 
                      type="text" 
                      class="form-control form-control-sm"
                      placeholder="Front text" />
                  </div>
                  <div class="flashcard-back text-muted mb-1">
                    <input 
                      v-model="flashcard.back" 
                      type="text" 
                      class="form-control form-control-sm"
                      placeholder="Back text" />
                  </div>
                </div>
                <div class="flashcard-content flex-grow-1" v-else>
                  <div class="flashcard-front mb-2">
                    <strong v-html="flashcard.annotated_front"></strong>
                  </div>
                  <div class="flashcard-back text-muted mb-1">
                    {{ flashcard.back }}
                  </div>
                </div>
                <div class="flashcard-actions">
                  <button 
                    @click.stop="editFlashcard(flashcard)"
                    class="btn btn-sm btn-outline-primary"
                    v-if="!flashcard.editing"

                  >
                    <i class="bi bi-pencil"></i>
                  </button>
                  <button 
                    @click.stop="saveFlashcard(flashcard)"
                    class="btn btn-sm btn-outline-secondary"
                    v-if="flashcard.editing"
                    >
                    <i class="bi bi-check"></i>
                  </button>
                  <button 
                    @click.stop="toggle_heart(flashcard)" 
                    class="btn btn-sm btn-outline-primary"
                    :title="flashcard.saved ? t('vocab.remove-from-saved') : t('vocab.add-to-saved')"
                  >
                    <i 
                      :class="flashcard.saved ? 'bi-heart-fill' : 'bi-heart'" 
                      class="bi"
                      :style="{ color: flashcard.saved ? '#dc3545' : '#6c757d' }"
                    ></i>
                  </button>
                  <button 
                    @click.stop="playFlashcard(flashcard)"
                    class="btn btn-sm btn-outline-primary"
                  >
                    <i class="bi bi-play-circle"></i>
                  </button>
                  <button 
                    @click.stop="removeFlashcard(flashcard)"
                    class="btn btn-sm btn-outline-danger"
                    :disabled="isRemoving"
                    :title="t('common.delete') || 'Delete'"
                  >
                    <div v-if="isRemoving === flashcard.id" class="spinner-border spinner-border-sm" role="status">
                      <span class="visually-hidden">{{ t('chat.loading') }}</span>
                    </div>
                    <i v-else class="bi bi-trash"></i>
                  </button>
                </div>
              </div>
            </div>
        </div>
      </div>
    </div>
    
    <!-- Game Mode -->
    <div v-else-if="mode === 'game'" class="flashcards-game">
      <div class="d-flex justify-content-between align-items-center mb-0">
        <h4 class="mb-0">{{ t('chat.flashcards') }}</h4>
        <div class="btn-group">
          <button @click="emit('showChat')" class="btn btn-outline-primary btn-sm">
            <i class="bi bi-chat-dots"></i> {{ t('chat.show-chat') }}
          </button>
          <button @click="mode = 'list'" class="btn btn-outline-secondary btn-sm">
            <i class="bi bi-list"></i> {{ t('chat.list') || 'List' }}
          </button>
        </div>
      </div>
      
      <!-- Game Container -->
      <div class="game-container">
        <div v-if="isCreatingGame" class="text-center py-4">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">{{ t('chat.loading') }}</span>
          </div>
          <p class="mt-2">{{ t('chat.loading') }}</p>
        </div>
        <div v-else-if="flashcards.length > 0" class="simple-flashcard-game">
          <game-container
            :chatID="props.chatID"
            :flashcards="true"
            :quiz_sessionID="quiz_sessionID"
            @gameCompleted="onGameCompleted"
            @gameError="onGameError"
          />
        </div>
        <div v-else class="text-center py-4">
          <div class="alert alert-warning">
            <i class="bi bi-exclamation-triangle"></i>
            {{ t('common.error-occurred') }}
          </div>
          <button @click="mode = 'list'" class="btn btn-primary">
            {{ t('common.back') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, inject } from 'vue'
import { fetchWrapper, getBaseUrl } from '@/helpers/fetch-wrapper'
import { useI18n } from 'vue-i18n'
import { useProfileStore } from '@/stores/profile.store'
import { useAuthStore } from '@/stores/auth.store'

import GameContainer from '@/views/GameContainer.vue'
const profileStore = useProfileStore()


const { t } = useI18n()

const audiomanager = inject('audiomanager')

// Props
const props = defineProps({
  chatID: {
    type: [String, Number],
    required: true
  }
})

// Emits
const emit = defineEmits(['close', 'showChat', 'showArticle'])

// State
const mode = ref('list') // 'list' or 'game'
const isLoading = ref(true)
const isCreatingGame = ref(false)
const isClearing = ref(false)
const isRemoving = ref(null) // stores the ID of the flashcard being removed
const flashcards = ref([])
const quiz_sessionID = ref(null)

function play_audio(message) {
  audiomanager.play(message.audio).then(() => {
    console.log('audio played')
  // eslint-disable-next-line no-unused-vars
  }).catch(() => {
    // console.log('audio error', error)
    setTimeout(() => play_audio(message), 100)
  })
}
function editFlashcard(flashcard) {
  flashcard.editing = true
}
function saveFlashcard(flashcard) {
  flashcard.editing = false
  
  // Optional: Save changes to the backend if needed
  // This could be an API call to update the flashcard
  console.log('Flashcard saved:', flashcard)
  // endpoint is "vocab/edit-recent/<pk>/"
  // we save with a PATCH
  fetchWrapper.patch(`/vocab/edit-recent/${flashcard.id}/`, {
    word: flashcard.front,
    definition: flashcard.back
  }).then(response => {
    console.log('Flashcard updated successfully:', response)
  }).catch(error => {
    console.error('Error updating flashcard:', error)
    // Optional: Show error message to user
    alert(t('common.error-occurred') || 'An error occurred while saving the flashcard')
  })
}
function playFlashcard(flashcard) {
  const { user } = useAuthStore()
  const learning_language = profileStore.profile.learning_language

  flashcard.audio = getBaseUrl(`/tts/?lang=${learning_language}&text=${flashcard.front}&token=${user.token}`)
  setTimeout(() => play_audio(flashcard), 100)
}

// Heart toggle functions
async function toggle_heart(flashcard) {
  flashcard.saved = !flashcard.saved
  if (flashcard.saved) {
    await add_vocab_heart(flashcard)
  } else {
    await delete_vocab_heart(flashcard)
  }
}

async function add_vocab_heart(flashcard) {
  flashcard.saved = true
  const learning_language = profileStore.profile.learning_language
  const native_language = profileStore.profile.native_language
  await fetchWrapper.post(`/vocab/add/`, { 
    word: flashcard.front, 
    definition: flashcard.back, 
    learning_language: learning_language, 
    native_language: native_language 
  })
}

async function delete_vocab_heart(flashcard) {
  flashcard.saved = false
  const learning_language = profileStore.profile.learning_language
  const native_language = profileStore.profile.native_language
  await fetchWrapper.post(`/vocab/remove/`, { 
    word: flashcard.front, 
    learning_language: learning_language,
    native_language: native_language
  })
}

// Methods
async function fetchFlashcards() {
  try {
    isLoading.value = true
    
    // Use new API endpoint to get flashcards for this specific chat
    const response = await fetchWrapper.get(`/chat/${props.chatID}/flashcards/`)
    
    if (response.flashcards && response.flashcards.length > 0) {
      flashcards.value = response.flashcards
      
      response.flashcards.forEach(card => {
        card.editing = false
      })
    } else {
      // No flashcards found for this chat
      flashcards.value = []
    }
  } catch (error) {
    console.error('Error fetching flashcards for chat:', error)
    flashcards.value = []
  } finally {
    isLoading.value = false
  }
}

async function selectFlashcard(flashcard) {
  // Optional: Handle individual flashcard selection
  console.log('Selected flashcard:', flashcard)
}


async function clearFlashcards() {
  if (!flashcards.value.length) {
    return
  }
  
  try {
    isClearing.value = true
    
    // Call API to clear flashcards for this chat using the correct endpoint and method
    await fetchWrapper.post(`/chat/${props.chatID}/flashcards/clear/`, {
      clear_all: true
    })
    
    // Clear local state
    flashcards.value = []
    
    // Optional: Show success message
    console.log('Flashcards cleared successfully')
    
  } catch (error) {
    console.error('Error clearing flashcards:', error)
    // Optional: Show error message to user
    alert(t('common.error-occurred') || 'An error occurred while clearing flashcards')
  } finally {
    isClearing.value = false
  }
}

async function removeFlashcard(flashcard) {
  if (!flashcard.id) {
    return
  }
  
  try {
    isRemoving.value = flashcard.id
    
    // Call API to remove individual flashcard using the clear endpoint with recent_vocabID
    await fetchWrapper.post(`/chat/${props.chatID}/flashcards/clear/`, {
      recent_vocabID: flashcard.id
    })
    
    // Remove from local state
    flashcards.value = flashcards.value.filter(card => card.id !== flashcard.id)
    
    // Update groups if necessary
    const groupMap = new Map()
    flashcards.value.forEach(card => {
      if (card.groupId && card.groupName) {
        groupMap.set(card.groupId, {
          id: card.groupId,
          name: card.groupName,
          subscribed: true,
          num_cards: groupMap.has(card.groupId) ? groupMap.get(card.groupId).num_cards + 1 : 1
        })
      }
    })
    
    console.log('Flashcard removed successfully')
    
  } catch (error) {
    console.error('Error removing flashcard:', error)
    // Optional: Show error message to user
    alert(t('common.error-occurred') || 'An error occurred while removing the flashcard')
  } finally {
    isRemoving.value = null
  }
}

async function startGame() {
  if (!flashcards.value.length) {
    return
  }
  
  try {
    isCreatingGame.value = true
    mode.value = 'game'
    
    // Use new API endpoint to create a game session for this chat's flashcards
    // note: just dummy endpoint for demo purposes
    const response = await fetchWrapper.post(`/chat/${props.chatID}/flashcards/start-game/`, {
    })
    
    if (response.quiz_sessionID) {
      quiz_sessionID.value = response.quiz_sessionID
    } else {
      // Fallback - let GameContainer handle it without a specific session
      quiz_sessionID.value = null
    }
  } catch (error) {
    console.error('Error starting flashcard game for chat:', error)
    // Even if the specific endpoint fails, try to let GameContainer handle it
    quiz_sessionID.value = null
  } finally {
    isCreatingGame.value = false
  }
}

function onGameCompleted() {
  mode.value = 'list'
  quiz_sessionID.value = null
  // Optionally refresh the flashcards
  fetchFlashcards()
}

function onGameError() {
  mode.value = 'list'
  quiz_sessionID.value = null
}

// Lifecycle
onMounted(async () => {
  await fetchFlashcards()
})

// Watch for mode changes
import { watch } from 'vue'
watch(mode, (newMode) => {
  if (newMode === 'game') {
    startGame()
  }
})
</script>

<style scoped>
.chat-flashcards-container {
  height: 100%;
  padding: 1rem;
}

.flashcards-list {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.flashcards-grid {
  flex: 1;
  overflow-y: auto;
  max-height: calc(100vh - 200px);
}

.flashcard-item {
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid #dee2e6;
}

.flashcard-item:hover {
  border-color: #007bff;
  box-shadow: 0 2px 4px rgba(0,123,255,0.1);
  transform: translateY(-1px);
}

.flashcard-content {
  min-height: 60px;
}

.flashcard-front {
  font-size: 1.1rem;
  color: #2c3e50;
}

.flashcard-back {
  font-size: 0.95rem;
  color: #6c757d;
}

.flashcard-actions {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 40px;
}

.flashcard-group {
  font-size: 0.7rem;
  margin-top: 0.25rem;
}

.flashcards-game {
  height: 100%;
}

.simple-flashcard-game {
  padding: 0;
}

.simple-flashcard-game code {
  background: #f8f9fa;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-family: 'Courier New', monospace;
}

.game-container {
  height: calc(100% - 60px);
  min-height: 400px;
}

.btn-group {
  display: flex;
  gap: 0.5rem;
}

.btn-group .btn {
  margin: 0;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .chat-flashcards-container {
    padding: 0.5rem;
  }
  
  .flashcards-grid {
    max-height: calc(100vh - 180px);
  }
  
  .btn-group {
    gap: 0.25rem;
  }
  
  .btn-group .btn {
    font-size: 0.875rem;
  }
}
</style>
